import { useHydratedSearchParams } from "@/lib/hooks/searchParams";
import { useId, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CloseIcon, PinIcon, SearchIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import { trackControl } from "@/lib/analytics";
import { SEARCH_ACTION } from "@/lib/landing";
import { OUTLINE_BUTTON } from "@/components/site/styles";
import { usePlaces } from "@/lib/hooks/catalogue";
import { useDebounced } from "@/lib/hooks/debounce";
import {
    ANYWHERE,
    locationLabel,
    matchingLocations,
    readJobLocation,
    writeJobLocation,
    type JobLocation,
} from "@/lib/jobLocation";

/**
 * Search destination, independent of the visitor's current location or saved envoy.
 * `site` is the front page's look: no card around it, a visible Location
 * label, taller fields, an outlined search button (the hero's Get free
 * autofill is the page's one green button), and no line about a saved
 * envoy, which a visitor does not have. `anywhere` is a search that opens
 * on every place: the empty field says so, there is no envoy to mention,
 * and a place picked can be taken off at once.
 */
export default function LocationSearch({
    children,
    searchWords,
    target,
    variant = "app",
    anywhere = false,
    id,
}: {
    children: ReactNode;
    searchWords?: string;
    target: string;
    variant?: "app" | "site";
    anywhere?: boolean;
    id?: string;
}) {
    const site = variant === "site";
    const [params] = useHydratedSearchParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const applied = readJobLocation(params);
    const appliedKey = JSON.stringify(applied);
    const [draft, setDraft] = useState(applied ? locationLabel(applied) : "");
    const [picked, setPicked] = useState<JobLocation | null>(applied);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const box = useRef<HTMLFormElement>(null);
    const list = useId();
    const settled = useDebounced(draft.trim());
    const q = settled.split(",")[0]?.trim() || undefined;
    const enabled = open && draft.trim().length >= 2;
    const cities = usePlaces({ level: "CITY", q, limit: 8 }, enabled);
    const parts = settled.split(",").map((part) => part.trim());
    const regions = usePlaces({ level: "REGION", q: parts[1] || q, limit: 5 }, enabled);
    const countries = usePlaces({ level: "COUNTRY", q: parts[2] || q, limit: 5 }, enabled);
    const settling = settled !== draft.trim();
    const loading =
        enabled && (settling || cities.isPending || regions.isPending || countries.isPending);
    const failed = enabled && cities.isError && regions.isError && countries.isError;
    const rows = settling
        ? []
        : matchingLocations(
              [
                  ...(cities.isPlaceholderData ? [] : (cities.data?.data ?? [])),
                  ...(regions.isPlaceholderData ? [] : (regions.data?.data ?? [])),
                  ...(countries.isPlaceholderData ? [] : (countries.data?.data ?? [])),
              ],
              draft,
              regions.data?.data,
          );
    const choices: JobLocation[] = [ANYWHERE, ...rows];

    const [seenApplied, setSeenApplied] = useState(appliedKey);
    if (seenApplied !== appliedKey) {
        setSeenApplied(appliedKey);
        const next = applied;
        setPicked(next);
        setDraft(next ? locationLabel(next) : "");
        setOpen(false);
        setActive(-1);
    }

    const choose = (place: JobLocation) => {
        setPicked(place);
        setDraft(place.level === "ANY" ? "" : locationLabel(place));
        setOpen(false);
        setActive(-1);
    };
    const nextPlace = () =>
        picked ??
        (draft.trim()
            ? {
                  level: "TEXT" as const,
                  value: draft.trim(),
                  name: draft.trim(),
                  country: null,
                  region: null,
              }
            : ANYWHERE);
    const submit = () => {
        const next = writeJobLocation(new URLSearchParams(search), nextPlace());
        if (searchWords !== undefined) {
            if (searchWords.trim()) next.set("q", searchWords.trim());
            else next.delete("q");
        }
        navigate({ pathname: target, search: next.toString() });
        setOpen(false);
    };
    const clear = () =>
        navigate({
            pathname: target,
            search: writeJobLocation(new URLSearchParams(search), null).toString(),
        });

    return (
        <form
            ref={box}
            {...actionProps("job_location_search_submitted", {
                source: "LocationSearch",
                mode: nextPlace().level,
                has_query: Boolean(searchWords ?? params.get("q")),
            })}
            id={id}
            role="search"
            aria-label="Jobs by title and location"
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                    setOpen(false);
            }}
            className={
                site
                    ? "grid w-full items-end gap-3 min-[900px]:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_auto]"
                    : "grid w-full gap-2.5 min-[900px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            }
        >
            {children}
            <div className="relative min-w-0">
                <label
                    className={
                        site
                            ? "flex flex-col gap-1.5 text-[15px] font-semibold text-fg"
                            : "flex h-11 items-center gap-2.5 rounded-control border border-line-strong bg-card px-3.5 text-fg-faint focus-within:ring-focus"
                    }
                >
                    {site ? (
                        <span>Location</span>
                    ) : (
                        <>
                            <PinIcon className="size-4 shrink-0" />
                            <span className="sr-only">City, state or province, country</span>
                        </>
                    )}
                    <span
                        className={
                            site
                                ? "flex h-12 items-center gap-2.5 rounded-control border border-line-control bg-card px-3.5 font-normal text-fg-faint focus-within:ring-focus"
                                : "contents"
                        }
                    >
                        {site && <PinIcon className="size-4 shrink-0" />}
                        <input
                            {...actionProps("job_location_edited", { source: "LocationSearch" })}
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={open}
                            aria-controls={list}
                            aria-activedescendant={
                                open && active >= 0 ? `${list}-${active}` : undefined
                            }
                            value={draft}
                            placeholder={anywhere ? "Anywhere" : "City, state or province, country"}
                            autoComplete="off"
                            maxLength={200}
                            onFocus={() => setOpen(true)}
                            onChange={(event) => {
                                setDraft(event.target.value);
                                setPicked(null);
                                setOpen(true);
                                setActive(-1);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                                    event.preventDefault();
                                    setOpen(true);
                                    setActive((current) =>
                                        current < 0
                                            ? event.key === "ArrowDown"
                                                ? 0
                                                : choices.length - 1
                                            : (current +
                                                  (event.key === "ArrowDown" ? 1 : -1) +
                                                  choices.length) %
                                              choices.length,
                                    );
                                } else if (event.key === "Escape") {
                                    event.preventDefault();
                                    setOpen(false);
                                    setActive(-1);
                                } else if (
                                    event.key === "Enter" &&
                                    open &&
                                    active >= 0 &&
                                    choices[active]
                                ) {
                                    event.preventDefault();
                                    trackControl("job_location_selected", {
                                        source: "LocationSearch",
                                        option: locationLabel(choices[active]),
                                        mode: choices[active].level,
                                        interaction: "keydown",
                                    });
                                    choose(choices[active]);
                                }
                            }}
                            className={`min-w-0 flex-1 border-0 bg-transparent text-fg outline-none placeholder:text-fg-faint ${site ? "text-[15px]" : "text-[13px]"}`}
                        />
                        {anywhere && applied && applied.level !== "ANY" && (
                            <button
                                {...actionProps("job_location_cleared", {
                                    source: "LocationSearch",
                                })}
                                type="button"
                                onClick={clear}
                                aria-label="Clear location"
                                className="grid size-6 shrink-0 place-items-center rounded-md bg-card-raised text-fg-muted hover:text-fg"
                            >
                                <CloseIcon className="size-3" strokeWidth={2.2} />
                            </button>
                        )}
                    </span>
                </label>
                {open && (
                    <div
                        id={list}
                        role="listbox"
                        aria-label="Suggested job locations"
                        className="absolute inset-x-0 top-[calc(100%+6px)] z-30 max-h-72 overflow-y-auto rounded-card border border-line bg-card p-1.5 text-fg shadow-pop"
                    >
                        {choices.map((place, index) => (
                            <button
                                key={`${place.level}:${place.value}`}
                                id={`${list}-${index}`}
                                type="button"
                                role="option"
                                aria-selected={active === index}
                                tabIndex={-1}
                                {...actionProps("job_location_selected", {
                                    source: "LocationSearch",
                                    option: locationLabel(place),
                                    mode: place.level,
                                })}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => choose(place)}
                                className={`flex w-full flex-col gap-0.5 rounded-[9px] px-3 py-2 text-left text-[13px] hover:bg-hover ${active === index ? "bg-hover" : ""}`}
                            >
                                <span className="font-medium">{locationLabel(place)}</span>
                                <span className="text-[11px] text-fg-faint">
                                    {place.level === "CITY"
                                        ? "City"
                                        : place.level === "REGION"
                                          ? "State or province"
                                          : place.level === "COUNTRY"
                                            ? "Country"
                                            : "Remove location restrictions"}
                                </span>
                            </button>
                        ))}
                        <div role="status" className="px-3 py-2 text-[11.5px] text-fg-muted">
                            {loading
                                ? "Finding locations…"
                                : failed
                                  ? "Location suggestions are unavailable. You can still type a location and search."
                                  : draft.trim().length < 2
                                    ? "Type at least two characters for suggestions."
                                    : rows.length === 0
                                      ? "No matching suggestions. Search will use the location you typed."
                                      : "Pick a place, then search."}
                        </div>
                    </div>
                )}
            </div>
            <button
                {...actionProps("job_location_search_clicked", {
                    source: "LocationSearch",
                    mode: nextPlace().level,
                    has_query: Boolean(searchWords ?? params.get("q")),
                })}
                type="submit"
                className={
                    site
                        ? `${OUTLINE_BUTTON} h-12 gap-2.5 rounded-control px-8 text-[15px]`
                        : "h-11 rounded-control bg-grad-btn px-5 text-[13px] font-semibold text-brand-fg shadow-glow"
                }
            >
                {site && <SearchIcon className="size-4" />}
                {site ? SEARCH_ACTION : "Search jobs"}
            </button>
            {!site && !anywhere && (
                <p className="m-0 text-[11.5px] text-fg-muted min-[900px]:col-span-3">
                    Choose where you want to work. This only changes your search, not your saved
                    envoy.
                </p>
            )}
        </form>
    );
}
